import React, { useState } from "react";
import StepHeading from "../../common/stepHeading/StepHeading";
import StepTitle from "../../common/stepTitle/StepTitle";
import LanguageSelector from "../../common/languageSelector/LanguageSelector";
import TextArea from "carbon-react/lib/__experimental__/components/textarea";
import DialogConfirmButtons from "../../common/dialogConfirmButtons/DialogConfirmButtons";
import Content from "carbon-react/lib/components/content";
import ConfirmDialog from "../../common/confirmDialog/ConfirmDialog";

const RequestResetStep = ({
  user,
  busy,
  state,
  onStateChanged,
  onCancel,
  onCompleted,
}) => {
  const [confirmResetDialogOpen, setConfirmResetDialogOpen] = useState(false);

  const isAValidFunc = (func) => {
    return func && typeof func === "function";
  };

  const validateState = () => {
    let valid = true;

    if (!validateLanguageCode()) valid = false;
    if (!validateAgentComments()) valid = false;
    return valid;
  };

  const validateLanguageCode = () => {
    const valid = state.languageCode && state.languageCode.length > 0;
    updateState(() => {
      state.languageCodeValid = valid;
    });
    return valid;
  };

  const onLanguageCodeChanged = (languageCode) => {
    updateState(() => {
      state.languageCodeValid = true;
      state.languageCode = languageCode;
    });
  };

  const onAgentCommentsChanged = (agentComments) => {
    const sanitizedComments =
      agentComments && agentComments.length > 0 ? agentComments : "";
    updateState(() => {
      state.agentCommentsValid = true;
      state.agentComments = sanitizedComments;
    });
  };

  const updateState = (mutateState) => {
    if (isAValidFunc(mutateState)) mutateState();
    onStateChanged({ ...state });
  };

  const validateAgentComments = () => {
    const sanitizedComments = state.agentComments
      ? state.agentComments.trim()
      : "";
    const valid = sanitizedComments.length > 0 ? true : false;
    updateState(() => {
      state.agentComments = sanitizedComments;
      state.agentCommentsValid = valid;
    });
    return valid;
  };

  const languageSelector = (
    <LanguageSelector
      id="reset-password-select-language"
      required={true}
      appearBusy={busy}
      value={state.languageCode}
      autoFocus={true}
      onValueChanged={(languageCode) => {
        onLanguageCodeChanged(languageCode);
      }}
      onBlur={validateLanguageCode}
      appearInvalid={!state.languageCodeValid}
      validationMsg="Language must be provided"
    />
  );

  const agentCommentsWarningAttributes = {
    hasWarning: true,
    inputIcon: "warning",
    tooltipMessage: "Please indicate why this action is being performed",
  };

  const agentCommentsInput = (
    <TextArea
      id="reset-password-agent-comments"
      placeholder={agentCommentsWarningAttributes.tooltipMessage}
      label="Comments (Required)"
      disabled={busy}
      value={state.agentComments}
      characterLimit={"250"}
      enforceCharacterLimit={true}
      name="changeEmailComments"
      onChange={(e) => {
        onAgentCommentsChanged(e.target.value);
      }}
      onBlur={validateAgentComments}
      {...(!state.agentCommentsValid ? agentCommentsWarningAttributes : {})}
    />
  );

  const passwordChangeDisclaimerOne =
    "This feature will change the user's password to an automatically generated" +
    " temporary value.  When logging in with this temporary password for the first" +
    " time, the user will be prompted to provide a new password of their chosing.";

  const onResetPassword = () => {
    if (!validateState()) return;
    setConfirmResetDialogOpen(true);
  };

  const onCancelClicked = () => {
    if (isAValidFunc(onCancel)) onCancel();
  };

  const dialogButtons = (
    <DialogConfirmButtons
      appearBusy={busy}
      confirmBtnEnabled={true}
      confirmBtnLabel="Reset Password"
      onConfirm={onResetPassword}
      onCancel={onCancelClicked}
    />
  );

  const onConfirmResetCancel = () => {
    setConfirmResetDialogOpen(false);
    onCancelClicked();
  };

  const onConfirmResetAccept = () => {
    setConfirmResetDialogOpen(false);
    if (isAValidFunc(onCompleted)) onCompleted();
  };

  const confirmDialog = (
    <ConfirmDialog
      open={confirmResetDialogOpen}
      prompt="Are you sure you want to reset the password for this user?  
      Once reset, the new/temporary password MUST be relayed to the customer"
      confirmBtnLabel="Reset"
      onConfirm={onConfirmResetAccept}
      onCancel={onConfirmResetCancel}
    />
  );

  return (
    <>
      <div className="reset-password-dialog-body">
        <StepTitle title="Resetting a user's password will impact ALL applications secured via this identity" />
        <StepHeading label="Current Email" value={user?.email} />
        <Content>{passwordChangeDisclaimerOne}</Content>
        <StepTitle
          className="reset-password-disclaimer"
          title="Maintain contact with the user until the temporary password has been relayed to them VERBALLY.  The new password will NOT be emailled to the user."
        />
        {languageSelector}
        {agentCommentsInput}
      </div>
      {dialogButtons}
      {confirmDialog}
    </>
  );
};

export default RequestResetStep;
